import each from 'lodash/each';
import loadFiles from '../helpers/file-loader';
import { uimDefaultTokenSource } from '../helpers/default-token-sources';
import NewRelicLogProvider, { setAppContext } from './new-relic-provider';
import getAppContext from '../helpers/get-app-context';
import { getCdnBasePath } from './paths';
import { convertConfig } from '../helpers/configuration-transformation';

let globalLoadedFilesList = new Set();

/**
 * Reset the saved list of loaded files
 */
function resetInternal() {
    globalLoadedFilesList = new Set();
}

/**
 * Get the list of already loaded files
 */
function getLoadedFiles() {
    return globalLoadedFilesList;
}

/**
 * Initialise application config
 * @param {*} loaderConfig
 */
function initAppConfig(loaderConfig) {
    if (window.mwc.configuration && typeof window.mwc.configuration.setAppConfig === 'function') {
        let { config } = loaderConfig;
        if (loaderConfig.configuration && loaderConfig.configuration.format === 'ec' && !window.morningstar.asterix) {
            config = convertConfig(config, 'mwc');
        }
        window.mwc.configuration.setAppConfig(config);
    }
}

/**
 * Register specified token sources
 * @param {*} loaderConfig
 */
function registerTokenSources(loaderConfig) {
    if (window.mwc.dataAccess && typeof window.mwc.dataAccess.registerTokenSource === 'function' && loaderConfig.api) {
        if (loaderConfig.api.tokenSources) {
            loaderConfig.api.tokenSources.forEach((source) => {
                window.mwc.dataAccess.registerTokenSource(source);
            });
        }

        if (loaderConfig.api.apiTokenExpiredCallback) {
            const wrappedTokenExpiredCallback = (callbackFromDataAccess, options) => {
                const wrappedCallback = (tokenName, tokenValue) => {
                    callbackFromDataAccess(null, tokenValue);
                };
                loaderConfig.api.apiTokenExpiredCallback(options, wrappedCallback);
            };
            uimDefaultTokenSource.refreshToken = wrappedTokenExpiredCallback;
            if (loaderConfig.apiTokens) {
                uimDefaultTokenSource.token = loaderConfig.apiTokens.uim || loaderConfig.apiTokens.apiGatewayToken;
            }
            window.mwc.dataAccess.registerTokenSource(uimDefaultTokenSource);
        }
    }
}

/**
 * Initialise specified usage trackers and Morningstar usage tracking
 * @param {*} loaderConfig
 */
function initUsageTracking(loaderConfig) {
    if (window.mwc.logger && typeof window.mwc.logger.addLogProvider === 'function' && loaderConfig.usageTracking) {
        Object.keys(loaderConfig.usageTracking).forEach((providerKey) => {
            const { provider } = loaderConfig.usageTracking[providerKey];
            if (typeof provider === 'string' && provider === 'newRelic') {
                window.mwc.logger.addLogProvider(new NewRelicLogProvider(), 'newRelic', 99);
            }
        });
    }

    if (loaderConfig.morningstarUsageTracking && loaderConfig.morningstarUsageTracking.enabled) {
        setAppContext(getAppContext(loaderConfig));
    }
}

/**
 * Prepare the mwc application based on the loader config
 * @param {*} loaderConfig
 */
function prepareMwc(loaderConfig) {
    if (window.mwc) {
        initAppConfig(loaderConfig);
        registerTokenSources(loaderConfig);
        initUsageTracking(loaderConfig);
    }
}

/**
 * Load the mwc-application-helper if requested and prepare mwc based on the loader config
 * @param {*} loaderConfig
 */
function mwcReady(loaderConfig) {
    return new Promise((resolve, reject) => {
        const mwcHelperConfig = loaderConfig.mwc && loaderConfig.mwc['mwc-application-helper'];

        if (mwcHelperConfig) {
            const mwcHelperPath = [`${getCdnBasePath(mwcHelperConfig.environment)}mwc/mwc-application-helper/${mwcHelperConfig.version}/dist/mwc-application-helper.js`];
            loadFiles(mwcHelperPath, null)
                .then((loadedList) => {
                    Object.keys(loadedList).forEach((file) => {
                        globalLoadedFilesList.add(file);
                    });
                    prepareMwc(loaderConfig);
                    resolve();
                }).catch((error) => {
                    reject(error);
                });
        } else {
            prepareMwc(loaderConfig);
            resolve();
        }
    });
}

/**
 * Create component tag
 * @param {string} tagName - components tag name
 * @param {*} morningstarComponentId - morningstar component id
 * @param {*} container - the element where we will place the new component tag
 * @param {string} type - the component type: ec or mwc
 */
function createComponentTag(tagName, morningstarComponentId, container, type) {
    if (type === 'ec') {
        const componentNode = document.createElement(tagName);
        componentNode.setAttribute('mstar-component-id', morningstarComponentId);
        if (window.morningstar.asterix.util.isDomElement(container)) {
            container.appendChild(componentNode);
        }
        return componentNode;
    } else if (type === 'mwc') {
        const componentNode = document.createElement(tagName);
        componentNode.setAttribute('mwc-id', morningstarComponentId);
        container.appendChild(componentNode);
        return componentNode;
    }
    throw new Error(`Specified type '${type}' does not exist.`);
}

/**
 * Bootstrap the elements declared in elementBindings
 * @param {*} loaderConfig
 */
function bootstrap(loaderConfig) {
    const getElementType = (tag) => {
        if (tag.startsWith('ec') && tag.indexOf('mwc-') === -1) {
            return 'ec';
        }
        return 'mwc';
    };

    return new Promise((resolve) => {
        each(loaderConfig.elementBindings, (elementBinding) => {
            const type = getElementType(elementBinding.tagName);
            const componentNode = createComponentTag(elementBinding.tagName, elementBinding.morningstarComponentId, elementBinding.container, type);

            if (window.angular && type === 'ec' && window.morningstar.asterix && window.morningstar.asterix.ng) {
                window.angular.element(document).ready(() => {
                    window.angular.bootstrap(componentNode, window.morningstar.asterix.ng.getAngularModuleDependencies());
                });
            }
        });
        resolve();
    });
}

export {
    bootstrap,
    getLoadedFiles,
    mwcReady,
    resetInternal,
};



// WEBPACK FOOTER //
// ../loader-base/src/services/component-loader.js